import { ThemeEvents } from '@theme/events';

const MIN_SHIMMER_MS = 300;
const MAX_SHIMMER_MS = 800;
const shimmerState = new WeakMap();

const getCountValue = (section) => {
  const count = section.querySelector('.products__result text-component');
  if (!count) return '';
  return count.getAttribute('value') || count.textContent?.trim() || '';
};

const getCountText = (section) => {
  const count = section.querySelector('.products__result text-component');
  if (!count) return '';
  return count.textContent?.trim() || '';
};

const lockCountValue = (section, value) => {
  const count = section.querySelector('.products__result text-component');
  if (!count) return;

  count.setAttribute('value', value);
  count.textContent = value;
};

const getGridSignature = (section) => {
  const grid = section.querySelector('.product-grid');
  if (!grid) return '';

  const cards = grid.querySelectorAll('.product-card');
  const firstLink = cards[0]?.querySelector('.product-card__link')?.getAttribute('href') || '';
  const lastLink = cards[cards.length - 1]?.querySelector('.product-card__link')?.getAttribute('href') || '';
  return `${cards.length}|${firstLink}|${lastLink}`;
};

const applyCatalogShimmer = (section) => {
  const grid = section.querySelector('.product-grid');
  if (grid) {
    grid.setAttribute('data-shimmer', '');
  }

  const count = section.querySelector('.products__result text-component');
  if (count) {
    if (typeof count.shimmer === 'function') {
      count.shimmer();
    } else {
      count.setAttribute('shimmer', '');
    }
  }
};

const clearShimmerTimers = (state) => {
  if (state.resetTimer) {
    clearTimeout(state.resetTimer);
  }
  if (state.maxTimer) {
    clearTimeout(state.maxTimer);
  }
};

const applyNextCount = (section, state) => {
  if (!state.nextCountValue) return;
  const count = section.querySelector('.products__result text-component');
  if (!count) return;

  count.setAttribute('value', state.nextCountValue);
  count.textContent = state.nextCountValue;
};

const resetCatalogShimmer = (section) => {
  const state = shimmerState.get(section);
  if (!state) return;

  clearShimmerTimers(state);
  state.observer?.disconnect();

  applyNextCount(section, state);

  const grid = section.querySelector('.product-grid');
  if (grid) {
    grid.removeAttribute('data-shimmer');
  }

  const count = section.querySelector('.products__result text-component');
  if (count) {
    count.removeAttribute('shimmer');
  }

  shimmerState.delete(section);
};

const scheduleReset = (section, state) => {
  if (state.resetTimer) return;

  const elapsed = Date.now() - state.startedAt;
  const remaining = Math.max(MIN_SHIMMER_MS - elapsed, 0);

  applyCatalogShimmer(section);
  state.resetTimer = setTimeout(() => resetCatalogShimmer(section), remaining);
};

document.addEventListener(ThemeEvents.FilterUpdate, (event) => {
  if (!(event.target instanceof Element)) return;

  const section = event.target.closest('.shopify-section');
  if (!section) return;

  const previousState = shimmerState.get(section);
  if (previousState) {
    resetCatalogShimmer(section);
  }

  const state = {
    startedAt: Date.now(),
    countValue: getCountValue(section),
    gridSignature: getGridSignature(section),
    nextCountValue: '',
    resetTimer: null,
    maxTimer: null,
    observer: null,
  };

  applyCatalogShimmer(section);

  state.observer = new MutationObserver(() => {
    const nextCountValue = getCountValue(section);
    const nextCountText = getCountText(section);
    const nextGridSignature = getGridSignature(section);

    const nextCandidate =
      nextCountText && nextCountText !== state.countValue
        ? nextCountText
        : nextCountValue && nextCountValue !== state.countValue
          ? nextCountValue
          : '';

    if (!state.nextCountValue && nextCandidate) {
      state.nextCountValue = nextCandidate;
    }

    if (
      (nextCountText && nextCountText !== state.countValue) ||
      (nextCountValue && nextCountValue !== state.countValue)
    ) {
      lockCountValue(section, state.countValue);
    }

    if (nextCountValue !== state.countValue || nextGridSignature !== state.gridSignature) {
      scheduleReset(section, state);
    }
  });

  state.observer.observe(section, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: ['value'],
  });

  state.maxTimer = setTimeout(() => scheduleReset(section, state), MAX_SHIMMER_MS);

  shimmerState.set(section, state);
});
